import React from 'react';
import { motion } from 'framer-motion';
import { 
  PiggyBank, 
  TrendingUp, 
  Calendar, 
  DollarSign,
  AlertTriangle,
  CheckCircle,
  Info
} from 'lucide-react';

interface ZUSPensionTrackerProps {
  currentAge: number;
  monthlyIncome: number;
  isZusRegistered: boolean;
  yearsWorked: number;
  totalContributions: number;
  estimatedPension: number;
  retirementAge: number;
  className?: string;
}

const ZUSPensionTracker: React.FC<ZUSPensionTrackerProps> = ({
  currentAge,
  monthlyIncome,
  isZusRegistered,
  yearsWorked,
  totalContributions,
  estimatedPension,
  retirementAge,
  className = ""
}) => {
  // Calculate ZUS contribution rate (simplified Polish system)
  const zusContributionRate = 0.1976; // 19.76% of gross income
  const monthlyContribution = monthlyIncome * zusContributionRate;
  const yearsToRetirement = Math.max(0, retirementAge - currentAge);

  // Calculate projected total contributions at retirement
  const projectedContributions = totalContributions + (monthlyContribution * 12 * yearsToRetirement);

  // Simplified pension calculation (in real ZUS, it's more complex)
  const averageMonthlyContribution = projectedContributions / Math.max(1, yearsWorked + yearsToRetirement);
  const estimatedMonthlyPension = averageMonthlyContribution * 0.6; // Simplified formula

  // Determine pension adequacy
  const getPensionAdequacy = (pension: number) => {
    const minimumWage = 4242; // PLN minimum wage (simplified)
    const replacementRatio = pension / minimumWage;
    
    if (replacementRatio >= 1.0) return { level: 'Excellent', color: 'text-green-400', bg: 'bg-green-500/20' };
    if (replacementRatio >= 0.7) return { level: 'Good', color: 'text-yellow-400', bg: 'bg-yellow-500/20' };
    if (replacementRatio >= 0.5) return { level: 'Adequate', color: 'text-orange-400', bg: 'bg-orange-500/20' };
    return { level: 'Insufficient', color: 'text-red-400', bg: 'bg-red-500/20' };
  };

  const pensionAdequacy = getPensionAdequacy(estimatedMonthlyPension);

  // Calculate contribution history
  const contributionHistory = Array.from({ length: Math.min(yearsWorked, 10) }, (_, i) => {
    const year = currentAge - yearsWorked + i;
    const contribution = monthlyContribution * 12;
    return { year, contribution };
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-slate-800/50 rounded-2xl border border-slate-700 p-6 ${className}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl">
            <PiggyBank size={24} className="text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-white">ZUS Pension System</h3>
            <p className="text-sm text-slate-400">Public retirement savings</p>
          </div>
        </div>
        
        <div className={`px-3 py-1 rounded-full text-xs font-medium ${
          isZusRegistered 
            ? 'bg-green-500/20 text-green-400' 
            : 'bg-red-500/20 text-red-400'
        }`}>
          {isZusRegistered ? (
            <>
              <CheckCircle size={12} className="inline mr-1" />
              Registered
            </>
          ) : (
            <>
              <AlertTriangle size={12} className="inline mr-1" />
              Not Registered
            </>
          )}
        </div>
      </div>

      {/* Current Status */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        {/* Total Contributions */}
        <div className="p-3 bg-slate-700/50 rounded-xl border border-slate-600">
          <div className="flex items-center space-x-1 mb-1">
            <DollarSign size={14} className="text-green-400" />
            <span className="text-xs font-medium text-white">Total</span>
          </div>
          <div className="text-lg font-bold text-green-400">
            ${totalContributions.toLocaleString()}
          </div>
          <div className="text-xs text-slate-400">
            {yearsWorked}y
          </div>
        </div>

        {/* Monthly Contribution */}
        <div className="p-3 bg-slate-700/50 rounded-xl border border-slate-600">
          <div className="flex items-center space-x-1 mb-1">
            <TrendingUp size={14} className="text-blue-400" />
            <span className="text-xs font-medium text-white">Monthly</span>
          </div>
          <div className="text-lg font-bold text-blue-400">
            ${monthlyContribution.toLocaleString()}
          </div>
          <div className="text-xs text-slate-400">
            {Math.round(zusContributionRate * 100)}%
          </div>
        </div>

        {/* Years to Retirement */}
        <div className="p-3 bg-slate-700/50 rounded-xl border border-slate-600">
          <div className="flex items-center space-x-1 mb-1">
            <Calendar size={14} className="text-purple-400" />
            <span className="text-xs font-medium text-white">To Retire</span>
          </div>
          <div className="text-lg font-bold text-purple-400">
            {yearsToRetirement}
          </div>
          <div className="text-xs text-slate-400">
            Age {retirementAge}
          </div>
        </div>
      </div>

      {/* Pension Projection */}
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-white mb-2">Retirement Projection</h4>
        <div className={`p-3 rounded-xl border-2 ${pensionAdequacy.bg} border-slate-600`}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-white">Monthly Pension</span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${pensionAdequacy.color} ${pensionAdequacy.bg}`}>
              {pensionAdequacy.level}
            </span>
          </div>
          <div className={`text-xl font-bold ${pensionAdequacy.color} mb-1`}>
            ${estimatedMonthlyPension.toLocaleString()}
          </div>
          <div className="text-xs text-slate-400">
            {yearsWorked + yearsToRetirement} years total
          </div>
        </div>
      </div>


      {/* Important Information */}
      <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-xl">
        <div className="flex items-start space-x-2">
          <Info size={16} className="text-blue-400 mt-0.5" />
          <div>
            <h5 className="text-xs font-medium text-blue-400 mb-1">Key Points</h5>
            <ul className="text-xs text-slate-300 space-y-0.5">
              <li>• Mandatory for employment contracts</li>
              <li>• Higher income = higher pension</li>
              <li>• Career breaks reduce contributions</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Recommendations */}
      {!isZusRegistered && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-xl"
        >
          <div className="flex items-start space-x-2">
            <AlertTriangle size={16} className="text-red-400 mt-0.5" />
            <div>
              <h5 className="text-xs font-medium text-red-400 mb-1">Not Registered</h5>
              <p className="text-xs text-slate-300">
                Get a formal employment contract to start building pension contributions.
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default ZUSPensionTracker;
